<template>
    <BaseLink
        :href="data.return_url"
        class="border-b border-border p-6 h-32 flex justify-between items-center gap-4 w-full transition-colors group text-ink hover:bg-surface-hover"
        :data="{ chat_source: CHATSOURCE.PAST_CONVERSATIONS }">
        <div class="grow text-left">
            <p class="body-small text-ink-muted mb-1 date"><BaseDate :value="data.updated_at" /></p>
            <p v-if="!isRenaming" class="title-body fs-mask title">{{ truncate(data.title, { length: TRUNCATE_LENGTH, omission: "..." }) }}</p>
            <div v-else class="flex w-full gap-2 items-center" @click.prevent.stop>
                <BaseInput
                    :id="renameInputId"
                    :model-value="newTitle"
                    :aria-label="t('components.pastConversation.title')"
                    class="grow min-w-0"
                    :maxlength="120"
                    @update:model-value="newTitle = $event.trim()"
                    @keydown.enter.stop="handleTitleEnter" />
                <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('components.pastConversation.saveTitle')" :disabled="!newTitle" @click="handleSaveTitle">
                    <i class="bi bi-check text-xl" aria-hidden="true" />
                </BaseButton>
                <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('components.pastConversation.cancelRename')" @click="isRenaming = false">
                    <i class="bi bi-x text-xl" aria-hidden="true" />
                </BaseButton>
            </div>
        </div>
        <BaseDropdownMenu :sections="menuSections" side="left" align="center" :side-offset="10" @select="handleMenuSelect" @close-auto-focus="handleMenuCloseAutoFocus">
            <template #trigger>
                <BaseButton theme="tertiary" icon-only :aria-label="t('components.pastConversation.options')" @click.stop.prevent>
                    <i class="bi bi-three-dots text-xl/none" aria-hidden="true" />
                </BaseButton>
            </template>
        </BaseDropdownMenu>
        <div class="text-ink body-small whitespace-nowrap">
            <span class="hidden md:inline">{{ ctaText ?? t("components.pastConversation.startChat") }}</span
            ><i class="bi bi-arrow-right icon-bold ml-2" aria-hidden="true" />
        </div>
    </BaseLink>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseDropdownMenu from "~vue/components/BaseDropdownMenu.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { CHATSOURCE } from "~vue/utils/constants";
import { unlessComposing } from "~vue/utils/keyboard";
import truncate from "lodash/truncate";
import { computed, nextTick, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const TRUNCATE_LENGTH = 120;

const { data, deletionDisabled } = defineProps({
    deletionDisabled: { type: Boolean },
    data: { type: Object, required: true },
    ctaText: { type: String, default: undefined },
});

const emit = defineEmits(["delete", "rename"]);

const menuSections = computed(() => {
    const items = [{ id: "rename", type: "button", label: t("components.pastConversation.rename"), icon: "bi bi-pencil" }];
    // Delete is hidden when the user is on legal/retention hold; rename stays
    // available because the v1 server endpoint only blocks delete.
    if (!deletionDisabled) {
        items.push({ id: "delete", type: "button", label: t("common.delete"), icon: "bi bi-trash" });
    }
    return [{ id: "actions", items }];
});

const handleDeletePastConversation = () => {
    emit("delete", { kind: data.kind, hashid: data.hashid, uuid: data.uuid });
};

const renameInputId = useId();
const isRenaming = ref(false);
const newTitle = ref("");

const handleStartRename = () => {
    newTitle.value = data.title ?? "";
    isRenaming.value = true;
};

const handleMenuCloseAutoFocus = async (event) => {
    if (!isRenaming.value) {
        return;
    }

    event.preventDefault();
    await nextTick();
    // Reka removes its modal focus scope in a timer after this event.
    globalThis.setTimeout(() => {
        const input = document.getElementById(renameInputId);
        if (input instanceof HTMLInputElement) {
            input.focus();
            input.select();
        }
    }, 0);
};

const handleMenuSelect = (item) => {
    if (item.id === "rename") {
        handleStartRename();
    } else if (item.id === "delete") {
        handleDeletePastConversation();
    }
};

const handleSaveTitle = () => {
    if (!newTitle.value) {
        return;
    }
    isRenaming.value = false;
    emit("rename", { kind: data.kind, hashid: data.hashid, uuid: data.uuid, newTitle: newTitle.value });
};

const handleTitleEnter = unlessComposing(handleSaveTitle);
</script>
